import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  IconButton,
  Stack,
  TextField
} from '@mui/material'
import React, { Fragment, useRef } from 'react'
import { Controller, useFieldArray, useFormContext } from 'react-hook-form'

import { PasswordTextField } from 'components/common/PasswordTextField'
import Icon from 'components/Icon'
import { type StatusCheckFormValues } from 'components/status-checks/status-checks-form-utils'

export const ObservabilityToolHeaders: React.FC<{
  isCustomObservabilityTool?: boolean
}> = ({ isCustomObservabilityTool = false }) => {
  const headersRef = useRef<HTMLDivElement>(null)
  const { control, trigger } = useFormContext<StatusCheckFormValues>()
  const { fields, append, remove } = useFieldArray({
    name: 'headers',
    control
  })

  return (
    <Stack
      spacing={fields.length ? 2 : 0}
      sx={{ width: '100%' }}
      onBlur={(event) => {
        // Don't validate if we are switching focus to elements within the field array
        if (headersRef.current?.contains(event.relatedTarget)) return
        trigger('headers')
      }}
    >
      <Controller
        control={control}
        name="headers"
        render={({ field: _field, fieldState: { error } }) => (
          <FormControl fullWidth error={Boolean(error)}>
            <Box
              sx={{
                columnGap: 2,
                display: 'grid',
                gridTemplateColumns: isCustomObservabilityTool
                  ? '1fr 1fr auto'
                  : '1fr 1fr',
                flexGrow: 1,
                maxWidth: '100%',
                rowGap: 2
              }}
            >
              {fields.map(({ id }, index) => (
                <Fragment key={id}>
                  <Controller
                    control={control}
                    name={`headers.${index}.key`}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        {...field}
                        autoComplete="off"
                        data-testid={`observability-tool-authentication-header-key-input-${index}`}
                        disabled={!isCustomObservabilityTool}
                        error={Boolean(error)}
                        helperText={error?.message}
                        label="Key"
                        variant="outlined"
                      />
                    )}
                  />
                  <Controller
                    control={control}
                    name={`headers.${index}.value`}
                    render={({ field, fieldState: { error } }) => (
                      <PasswordTextField
                        {...field}
                        required
                        autoComplete="off"
                        data-testid={`observability-tool-authentication-header-value-input-${index}`}
                        error={Boolean(error)}
                        helperText={error?.message}
                        label="Value"
                        variant="outlined"
                      />
                    )}
                  />
                  {/* Only custom observability tools are allowed to add/remove custom headers */}
                  {isCustomObservabilityTool && (
                    <IconButton
                      color="error"
                      data-testid={`observability-tool-authentication-remove-header-button-${index}`}
                      size="large"
                      sx={{ alignSelf: 'flex-start' }}
                      onClick={() => {
                        remove(index)
                        trigger('headers')
                      }}
                    >
                      <Icon icon="circleTrash" />
                    </IconButton>
                  )}
                </Fragment>
              ))}
            </Box>
            {error && (
              <FormHelperText error={Boolean(error)}>
                {error.message}
              </FormHelperText>
            )}
          </FormControl>
        )}
      />
      {/* Only custom observability tools are allowed to add/remove custom headers */}
      {isCustomObservabilityTool && (
        <Button
          data-testid="observability-tool-authentication-add-header-button"
          startIcon={<Icon icon="plus" />}
          sx={{
            alignSelf: 'flex-start',
            gridRow: fields.length + 1,
            width: 'fit-content'
          }}
          variant="contained"
          onClick={() => append({ key: '', value: '' })}
        >
          Add Header
        </Button>
      )}
    </Stack>
  )
}
